import type { RouterOutputs } from "@repo/api";
import { useEffect, useState } from "react";
import { UserRole } from "@repo/database";
import { getDateFNSLocale } from "@repo/utils";
import { setDefaultOptions } from "date-fns";
import posthog from "posthog-js";
import { useShallow } from "zustand/react/shallow";

import SubscriptionDueBanner from "~/components/layout/subscription-due-banner";
import TrialEndBanner from "~/components/layout/trial-end-banner";
import { useModule } from "~/hooks/use-module";
import { usePlatform } from "~/hooks/use-platform";
import { UserContext } from "~/hooks/use-user";
import { api } from "~/lib/api";
import i18n from "~/lib/i18n";
import { useAuthStore } from "~/stores/use-auth-store";
import { useLoadingStore } from "~/stores/use-loading-store";

export type User = RouterOutputs["user"]["getUser"];

export interface UserSubscription {
  id: string;
  startDate: Date;
  periodEnd?: Date;
}

export interface UserModule {
  id: string;
  moduleId: string;
}

export interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const impersonateId = useAuthStore((state) => state.impersonateId);

  const { data: user, isFetching: isUserLoading } = api.user.getUser.useQuery(
    undefined,
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  );

  const { data: impersonatedUser } = api.user.getById.useQuery(
    impersonateId ?? undefined,
    { enabled: !!impersonateId },
  );

  const { platform } = usePlatform();

  const { modules } = useModule();

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  const modulesOnUser = modules.filter((module) =>
    currentUser?.subscription?.modules.includes(module.id),
  );

  useEffect(() => {
    if (isUserLoading) {
      setLoadingStage("user");
    } else {
      setLoadingStage(undefined);
    }
  }, [isUserLoading, setLoadingStage]);

  useEffect(() => {
    if (!user) return;

    i18n.changeLanguage(user.language).catch((e) => console.error(e));
    setDefaultOptions({
      locale: getDateFNSLocale(user.language),
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (platform === "IOS" || platform === "ANDROID") {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: "userId",
          data: {
            userId: user.id,
          },
        }),
      );
    }
  }, [platform, user]);

  useEffect(() => {
    if (!user) return;
    posthog.identify(user.id, { email: user.email, name: user.companyName });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // if (user.v2Date) {
    //   window.location.href = "https://v2.vrodex.app";
    //   if (platform === "IOS" || platform === "ANDROID") {
    //     window.ReactNativeWebView?.postMessage(
    //       JSON.stringify({
    //         event: "set-app-url",
    //         data: {
    //           url: "https://v2.vrodex.app",
    //         },
    //       }),
    //     );
    //   }
    // }
  }, [user]);

  useEffect(() => {
    if (!impersonatedUser) {
      if (user) {
        setCurrentUser(user);
      }
      return;
    }
    setCurrentUser(impersonatedUser);
  }, [impersonatedUser, user]);
  if (!currentUser) return;

  const context = {
    user: currentUser,
    isAdmin: user?.role === UserRole.admin,
    subscriptions: [],
    modules: modulesOnUser.map((module) => module.type),
    trialEnded: currentUser.subscription?.trialEndAt
      ? new Date(currentUser.subscription.trialEndAt).getTime() <
        new Date().getTime()
      : false,
  };

  return (
    <UserContext.Provider value={context}>
      {currentUser.subscription?.trialEndAt && <TrialEndBanner />}
      {currentUser.subscription?.periodEnd && <SubscriptionDueBanner />}
      {children}
    </UserContext.Provider>
  );
};
